// New file contents for /home/bigbrain/projects/smashparty/src/shared/assets/loader-types.ts
// (copy-cite of AssetEntry from /home/bigbrain/projects/smashparty/src/shared/assets/manifest-schema.ts
//  lines 7–60 re-referenced; DO NOT duplicate AssetEntry — re-import it.)

import type { AssetEntry, AssetManifest, QcStatus } from './manifest-schema';

/**
 * Loader-side priority bucket. Lower number = load sooner.
 * Priorities are a pure function of AssetEntry.category (see `priorityForEntry`).
 */
export type LoaderPriority = 0 | 1 | 2 | 3 | 4;

/**
 * Flattened subset of AssetEntry that the browser loader actually reads. All fields
 * are derived from AssetEntry — this type exists so tests and consumers don't have
 * to import every optional QC field.
 */
export interface LoaderManifestEntry {
  readonly id: string;
  readonly category: AssetEntry['category'];
  readonly path: string;
  readonly placeholder: boolean;
  readonly placeholderSilence: boolean; // false when undefined/non-audio per manifest-schema.ts line 43
  readonly bytes: number;
  readonly dracoCompressed: boolean;
  readonly inlineBase64: boolean; // false when undefined (Plan 8c only flagged)
  readonly qcStatus: QcStatus | undefined;
  readonly priority: LoaderPriority;
}

/**
 * Loader-side manifest: flattened entries + the same summary as AssetManifest.
 */
export interface LoaderManifest {
  readonly schemaVersion: 1;
  readonly generatedAt: string;
  readonly entries: ReadonlyArray<LoaderManifestEntry>;
  readonly byId: ReadonlyMap<string, LoaderManifestEntry>;
  readonly summary: AssetManifest['summary'];
}

/**
 * Pure function mapping category → priority. Higher-frequency-of-view categories
 * load first. Critical path: character_body/character_animation/character_head/tile_icon/ui_sprite
 * (player-visible on every frame) → priority 0. Map textures and board objects
 * (visible once the scene lifts) → 1. Items/capes/effects (visible on use) → 2.
 * Minigame icons + decorations (optional surface) → 3. Audio → 4 (background
 * decoded, independent of visual load).
 */
export function priorityForCategory(category: AssetEntry['category']): LoaderPriority {
  switch (category) {
    case 'character_body':
    case 'character_animation':
    case 'character_head':
    case 'tile_icon':
    case 'ui_sprite':
      return 0;
    case 'map_texture':
    case 'board_object':
    case 'trophy':
      return 1;
    case 'item':
    case 'item_icon':
    case 'cape_texture':
    case 'eye_sheet':
    case 'emote_icon':
    case 'shape_icon':
    case 'npc':
      return 2;
    case 'minigame_icon':
    case 'decoration':
    case 'hy3d_concept':
    case 'character_head_source':
      return 3;
    case 'sfx':
    case 'music':
      return 4;
    default: {
      // Exhaustiveness check. If a new category is added to AssetEntry['category']
      // without updating this mapping, this line becomes a compile error.
      const _exhaustive: never = category;
      throw new Error(`priorityForCategory: unmapped category ${_exhaustive as string}`);
    }
  }
}
